import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DateRange } from '@bambi/component-library/src/components/inputs/DatePicker/DatePicker';
import DateRangePicker from '@bambi/component-library/src/components/inputs/DateRangePicker/DateRangePicker';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { formatDateAsTodayOrMonthDay } from 'src/common/date/formatDateAsTodayOrMonthDay';
import { useLazyGetInvoicesExportListAsCsvQuery } from 'src/common/external/bambi-api/emptyApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { CoreFormFieldSwitch } from 'src/common/FormField/v2/core/CoreFormFieldSwitch';
import { MultiSelectPayerField } from 'src/common/MultiSelectPayerContextField';
import { ModalProps } from 'src/common/primitives/Modal';

type InvoiceExportForm = {
  dateRange: DateRange;
  payers: string[];
  forceCustomerToBePassengerName: boolean;
};

interface DownloadInvoicesQuickbooksCSVProps {
  onClose: ModalProps['onClose'];
}

export function DownloadInvoicesQuickbooksCSV({
  onClose,
}: DownloadInvoicesQuickbooksCSVProps) {
  const form = useForm<Partial<InvoiceExportForm>>({
    defaultValues: {
      forceCustomerToBePassengerName: false,
    },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [trigger] = useLazyGetInvoicesExportListAsCsvQuery();

  const handleClose = useCallback(() => {
    onClose?.();
    form.reset();
  }, [onClose, form]);

  const handleSubmit = form.handleSubmit(async (data) => {
    if (!data.dateRange?.from) {
      setError('Please select a start date');
      return;
    }
    setLoading(true);

    try {
      setError(undefined);
      const rangeStart = DateTime.fromJSDate(data.dateRange.from).toISODate();

      const toDate =
        data.dateRange.to && DateTime.fromJSDate(data.dateRange.to).toISODate();

      const sameDayRange = rangeStart === toDate;

      const rangeEnd = sameDayRange ? undefined : toDate;

      await trigger({
        rangeStart,
        rangeEnd,
        tripPayerIn: data.payers,
        format: 'csv',
        outputType: data.forceCustomerToBePassengerName
          ? 'quickbooks-self-pay'
          : undefined,
      }).unwrap();

      handleClose();
    } catch {
      const errMsg = 'There was an error downloading the data.';
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  });

  return (
    <div data-testid="download-invoices-quickbooks-csv">
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
      {loading ? (
        <div className="flex w-full flex-row justify-center">
          <div className="flex flex-col items-center gap-2">
            <LoadingIndicator />
            Gathering data
          </div>
        </div>
      ) : (
        <FormProvider {...form}>
          <div className="mt-2 flex flex-row items-start justify-between">
            <div>
              <div className="text-sm font-medium text-gray-700">
                Select the date range *
              </div>
              <div className="flex items-center justify-between gap-2 pt-2">
                <Controller
                  control={form.control}
                  name="dateRange"
                  render={({ field }) => {
                    let fromDate = '';
                    let toDate = '';

                    if (field.value?.from) {
                      fromDate = formatDateAsTodayOrMonthDay(
                        DateTime.fromJSDate(field.value.from).toISODate(),
                        true
                      );
                    }

                    if (field.value?.to) {
                      toDate = formatDateAsTodayOrMonthDay(
                        DateTime.fromJSDate(field.value.to).toISODate(),
                        true
                      );
                    }

                    return (
                      <DateRangePicker
                        side="left"
                        triggerElement={
                          <Button>
                            <AdjustmentsHorizontalIcon className="mr-2 h-4 w-4" />{' '}
                            {field.value
                              ? ` ${fromDate}${toDate ? ' - ' + toDate : ''}`
                              : 'Select Date'}
                          </Button>
                        }
                        onDateRangeChange={(r) => {
                          field.onChange(r);
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <div className="max-w-[50%]">
              <Controller
                name="forceCustomerToBePassengerName"
                control={form.control}
                render={({ field, fieldState }) => {
                  return (
                    <CoreFormFieldSwitch
                      label="Use Passenger Name as Customer?"
                      inputProps={{
                        onChange: field.onChange,
                        value: field.value,
                      }}
                      error={fieldState.error?.message}
                      helperText="Replaces the payer's name with the passenger's full name in the Customer column."
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="py-4">
            <MultiSelectPayerField
              fieldPath="payers"
              fieldLabel="Payers (leave empty to select all payers)"
              rules={{}}
              canSelectAll={false}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 pt-6">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              dataTestId="download-csv"
              variant="primary"
              onClick={handleSubmit}
            >
              Export as CSV
            </Button>
          </div>
        </FormProvider>
      )}
    </div>
  );
}
